/**
 * Shared conversation-attachment chip + lazy audio loader.
 *
 * Extracted verbatim from `ChatMessageList.tsx` (C-2 of
 * `ai-docs/lexide-agent-channel-tabs-impl-plan.md`) so the Agent-page channel
 * threads reuse the SAME player instead of a drifting copy — honoring "media
 * rides the existing `lx.chat_read_media`, no new media path". The chat surface
 * imports these back; behaviour there is unchanged.
 *
 * One additive loosening vs the in-chat original: `EchoAttachment.fetch` is
 * OPTIONAL. An unmappable channel scope (see `channelScopeToMediaParams`)
 * yields a name-only chip with no play affordance, rather than a broken fetch.
 */
import { useState } from "react";
import { Paperclip, Play, RotateCcw, Volume2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { useGatewayStore } from "@/stores/useGatewayStore";
import type { LxChatReadMediaParams } from "@/types/gateway";

/** The minimal attachment shape the chip + loader need. Assignable FROM the
 *  chat surface's `ChatMessage["attachments"][number]` (whose `fetch` is
 *  required) — the optionality lives only here, for channel rows whose scope
 *  cannot be mapped to media params. */
export interface EchoAttachment {
  name: string;
  mime?: string;
  fetch?: LxChatReadMediaParams;
}

/** IM inbound media keeps its STAGED name (`file_10---<uuid>.ogg`) unless the
 *  user explicitly archived it under a friendly name — the uuid soup is
 *  correct data but poor chrome. Detect the staged pattern and show a
 *  localized generic label instead ("Nota de voz" for audio); the real name
 *  stays on the tooltip + `data-attachment-name` (tests + debugging). */
const STAGED_NAME_RE = /^file_\d+---[0-9a-f]{8}-[0-9a-f-]{27,}\./i;

/** Runtime rows often omit `mime` — the serve side infers it by extension on
 *  read (conversation-store.ts: "the SPA infers otherwise"), so the chip must
 *  do the same or a real voice note gets no play affordance. */
const AUDIO_EXTS = new Set(["ogg", "oga", "opus", "mp3", "m4a", "aac", "wav", "webm", "flac"]);

export function isAudioAttachment(a: { name: string; mime?: string }): boolean {
  if (a.mime) return a.mime.startsWith("audio/");
  const ext = a.name.split(".").pop()?.toLowerCase() ?? "";
  return AUDIO_EXTS.has(ext);
}

/** Lazy bytes-fetch shared by the voice-note header + the attachment
 *  chip: first play click streams the media via `lx.chat_read_media`,
 *  then caches it as a data URL for the inline `<audio>` element. */
export function useAttachmentAudio(attachment: EchoAttachment) {
  const rpc = useGatewayStore((s) => s.rpc);
  const [src, setSrc] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "ready" | "error">("idle");

  const load = async () => {
    if (!rpc || !attachment.fetch || state === "loading" || state === "ready") return;
    setState("loading");
    try {
      const media = await rpc.lxChatReadMedia(attachment.fetch);
      setSrc(`data:${media.mime};base64,${media.bytesBase64}`);
      setState("ready");
    } catch {
      setState("error");
    }
  };

  return { src, state, load };
}

export function AttachmentEchoChip({ attachment }: { attachment: EchoAttachment }) {
  const { t } = useTranslation();
  const { src, state, load } = useAttachmentAudio(attachment);
  const isAudio = isAudioAttachment(attachment);
  // A play affordance only makes sense when we CAN fetch the bytes.
  const playable = isAudio && attachment.fetch != null;
  const displayName = STAGED_NAME_RE.test(attachment.name)
    ? t(isAudio ? "chat.message.attachmentVoiceNote" : "chat.message.attachmentReceivedFile")
    : attachment.name;

  return (
    <div
      data-testid="attachment-echo-chip"
      data-attachment-name={attachment.name}
      className="flex items-center gap-1.5 rounded border border-cream-300 bg-cream-100/40 px-2 py-1 text-caption text-coffee-700 dark:border-coffee-700 dark:bg-coffee-900/30 dark:text-cream-300"
    >
      {isAudio ? <Volume2 size={12} className="shrink-0" /> : <Paperclip size={12} className="shrink-0" />}
      <span className="max-w-[240px] truncate" title={attachment.name}>
        {displayName}
      </span>
      {playable && state !== "ready" && (
        <button
          type="button"
          onClick={load}
          disabled={state === "loading"}
          aria-label={
            state === "error"
              ? t("chat.message.attachmentRetry", { name: displayName })
              : t("chat.message.attachmentPlay", { name: displayName })
          }
          data-testid="attachment-echo-play"
          className="ml-1 inline-flex items-center text-gold-700 transition-colors hover:text-gold-600 disabled:opacity-50 dark:text-gold-400"
        >
          {state === "loading" ? (
            <RotateCcw size={12} className="animate-spin" />
          ) : state === "error" ? (
            <RotateCcw size={12} />
          ) : (
            <Play size={12} />
          )}
        </button>
      )}
      {playable && state === "ready" && src && (
        // eslint-disable-next-line jsx-a11y/media-has-caption -- voice-note playback; the transcript IS the caption, rendered in the bubble above
        <audio controls autoPlay src={src} className="ml-1 h-6 max-w-[220px]" />
      )}
    </div>
  );
}
